import { useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { learningService } from '../services/learningService';
import { detectSightWordResults, detectSightWordResultsFromWords } from '../utils/sightWordDetector';
import type { LetterPickerErrorData } from '../components/learning/LetterPicker';

/** Debounce delay before flushing accumulated results (ms) */
const FLUSH_DELAY = 3000;

/**
 * Accumulates sight word correctness data from LetterPicker sentences
 * and auto-flushes to the sight-words/mastery API.
 */
export function useSightWordCollector() {
    const { user } = useAuth();
    const resultsRef = useRef<Array<{ word: string; correct: boolean }>>([]);
    const flushingRef = useRef(false);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const userRef = useRef(user);
    userRef.current = user;

    const doFlush = useCallback(async () => {
        if (!userRef.current || resultsRef.current.length === 0 || flushingRef.current) return;
        flushingRef.current = true;

        const results = [...resultsRef.current];
        resultsRef.current = [];

        try {
            await learningService.submitSightWordMastery({ results });
        } catch {
            // Best-effort analytics — don't block UX
        } finally {
            flushingRef.current = false;
        }
    }, []);

    const scheduleFlush = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => doFlush(), FLUSH_DELAY);
    }, [doFlush]);

    const addSentence = useCallback((data: LetterPickerErrorData) => {
        const sightWordResults = detectSightWordResults(data);
        if (sightWordResults.length > 0) {
            resultsRef.current.push(...sightWordResults);
            scheduleFlush();
        }
    }, [scheduleFlush]);

    /** Accept typing-mode word results (word + isCorrect pairs) */
    const addWordResults = useCallback((wordResults: Array<{ word: string; isCorrect: boolean }>) => {
        const sightWordResults = detectSightWordResultsFromWords(wordResults);
        if (sightWordResults.length > 0) {
            resultsRef.current.push(...sightWordResults);
            scheduleFlush();
        }
    }, [scheduleFlush]);

    // Flush on unmount
    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            doFlush();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { addSentence, addWordResults, flush: doFlush };
}
